// Zapisovač historie naposledy otevřených NATIVE agend.
//
// Naslouchá jedinému neutrálnímu eventu jádra (`subscribeLayerOpened`) — tím pokryje VŠECHNA
// otevření native agendy bez ohledu na cestu (useNavigator, přímé kernel.openModal z ObjectCategory
// navigace, windowing „return to stack", shell menu…). Legacy okna (host `legacyFormAgenda`) se
// přeskakují — ta zapisuje do historie `MagicControlBridge` s korektním GlobalName/params.
//
// Titulek se bere z definice agendy (`resolveTitle`), parametry z open-params vrstvy (takže každá
// entita = vlastní záznam) a vrstva se naváže na záznam pro případné pozdější doplnění titulku.

import { useEffect } from 'react';

import { legacyFormAgenda } from '@/modules/legacy-form/legacyFormAgenda';
import { linkLayerToHistory, recordAgendaHistory } from '@/shared/lib/agendaHistory';
import type { LayerDescriptor, NavigationKernel } from '@/shared/navigation';
import { resolveTitle } from '@/shared/navigation/shell';
import { useNavigationContext } from '@/shared/navigation/react/useNavigationContext';

/**
 * Zapíše právě otevřenou vrstvu do historie agend (nebo ji přeskočí u legacy hostu).
 * Exportováno kvůli testovatelnosti bez React kontextu.
 */
export function recordOpenedLayer(kernel: NavigationKernel, layer: LayerDescriptor): void {
  // Legacy formuláře řeší bridge (kind `legacy`); jádro je otevírá přes host agendu `legacyFormAgenda`.
  if (layer.agendaId === legacyFormAgenda.id) return;
  // Chybové / neúplné vrstvy do historie nepatří.
  if (layer.status !== 'ready') return;

  const config = kernel.getRegistry().getById(layer.agendaId);
  const title = resolveTitle(layer, config);
  const historyId = recordAgendaHistory({
    kind: 'native',
    target: layer.agendaId,
    title,
    params: layer.openParams,
  });
  linkLayerToHistory(layer.layerId, historyId);
}

/** Napojí zapisovač historie na jádro. Mountuje se jednou v hlavním shellu. */
export function useAgendaHistoryRecorder(): void {
  const { kernel } = useNavigationContext();
  useEffect(
    () => kernel.subscribeLayerOpened((layer) => recordOpenedLayer(kernel, layer)),
    [kernel]
  );
}
